import React, { useState, useEffect, useRef } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';

import Confetti from 'react-confetti';
import { countries } from 'countries-list';

import axiosInstance from '../../config/axiosConfig';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';
import useCompaniesFacturationStore from '../../store/useCompaniesFacturationStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.billing.payments.subscription_payments.${i18nKey}`, params);

const PaymentDetails = ({ visible, onHide, credits, discountAmount, oldTotalCost, newTotalCost, isLoading, setIsLoading, paymentStatus }) => {

    const toast = useRef(null);

    const initialFacturationData = {
        email: '', phone_number: '', first_name: '', last_name: '', city: '', postal_code: '',
        billing_address: '', additional_billing_address: '', country: '',
    };

    const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);

    const [facturationData, setFacturationData] = useState(initialFacturationData);
    const [paymentSucceeded, setPaymentSucceeded] = useState(false);
    const [selectedFacturation, setSelectedFacturation] = useState(null);
    const [showCreateFacturationModal, setShowCreateFacturationModal] = useState(false);
    const [showSelectFacturationModal, setShowSelectFacturationModal] = useState(false);
    const [loadingCompaniesFacturation, setLoadingCompaniesFacturation] = useState(false);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);

    const companiesFacturation = useCompaniesFacturationStore((state) => state.companiesFacturation);
    const setCompaniesFacturation = useCompaniesFacturationStore((state) => state.setCompaniesFacturation);
    const addCompanyFacturation = useCompaniesFacturationStore((state) => state.addCompanyFacturation);
    const clearCompaniesFacturation = useCompaniesFacturationStore((state) => state.clearCompaniesFacturation);

    const countryOptions = Object.entries(countries).map(([code, { name }]) => ({ label: name, value: code }));

    const fetchFacturations = async (force = false) => {
        if (companiesFacturation?.length > 0 && !force) {
            const lastTimestamp = companiesFacturation[companiesFacturation.length - 1].timestamp;
            const oneHour = new Date().getTime() - 1000 * 60 * 60;

            if (lastTimestamp > oneHour) return;
            else clearCompaniesFacturation();
        }

        if (selectedFacturation) setSelectedFacturation(null);

        try {
            const response = await axiosInstance.get(`/dashboard/finances/${selectedCompany.company_id}/facturation/list`);
            setCompaniesFacturation(response.data.items);
        } catch (error) {
            console.error('Failed to fetch facturations:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue, veuillez réessayer plus tard.' });
        } finally {
            setLoadingCompaniesFacturation(false);
        }
    };

    const handleReloadCompaniesFacturation = () => {
        setLoadingCompaniesFacturation(true);
        fetchFacturations(true);
    };

    useEffect(() => {
        fetchFacturations();
    }, []);

    const handleCreateFacturation = async () => {
        try {
            const response = await axiosInstance.post(`/dashboard/finances/${selectedCompany.company_id}/facturation/create`, facturationData);
            addCompanyFacturation(response.data.facturation);
            setShowCreateFacturationModal(false);
            setFacturationData(initialFacturationData);
        } catch (error) {
            console.error('Failed to create facturation:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue, veuillez réessayer plus tard.' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFacturationData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCountryChange = (e) => setFacturationData((prev) => ({ ...prev, country: e.value }));

    useEffect(() => {
        if (paymentStatus !== false) {
            if (paymentStatus === 1) {
                setPaymentSucceeded(true);
            } else if (paymentStatus === 2) {
                setPaymentSucceeded(false);
                setShowPaymentFailedModal(true);
            }
        }
    }, [paymentStatus]);

    useEffect(() => {
        if (isLoading) return;
        if (!visible) {
            setPaymentSucceeded(false);
            onHide();
        }
    }, [visible]);

    const handlePayment = async () => {
        if (isLoading || !selectedFacturation) return;
        setIsLoading(true);

        try {
            const response = await axiosInstance.post(`/dashboard/finances/${selectedCompany.company_id}/createCreditsPayment`, {
                credits,
                facturation_id: selectedFacturation.facturation_id,
            });

            const { payment_url } = response.data;
            if (payment_url) {
                window.location.href = payment_url;
            } else {
                throw new Error('Failed to create payment.');
            }
        } catch (error) {
            console.error('Payment failed:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue, veuillez réessayer plus tard.' });
        } finally {
            setIsLoading(false);
        }
    };

    if (showPaymentFailedModal) return (
        <Dialog header={t('payment_cancelled')} dismissableMask={true} visible={showPaymentFailedModal} onHide={() => setShowPaymentFailedModal(false)} style={{ width: '100vw', height: '100vh', maxWidth: '800px', maxHeight: '400px' }} modal>
            <section className="modal-card-body">
                <h1 className="has-text-centered is-size-4 mb-5">{t('transaction_cancelled')}</h1>
                <p>{t('payment_failed_message')}</p>
                <div className="has-text-centered mt-5">
                    <button className="button is-info" onClick={() => { setShowPaymentFailedModal(false); window.location.href = window.location.href; }}>{t('payment_failed_close')}</button>
                </div>
            </section>
        </Dialog>
    );

    if (paymentSucceeded) return (
        <Dialog
            header={t('payment_success')}
            dismissableMask={true}
            visible={paymentSucceeded}
            onHide={() => { setPaymentSucceeded(false); window.location.href = window.location.href; }}
            style={{ width: '100vw', height: '100vh', maxWidth: '800px', maxHeight: '400px' }}
            modal
        >
            <section className="modal-card-body has-text-centered">
                <h1 className="title is-size-4 mb-5">{t('payment_success')}</h1>
                <p>{t('success_message')}</p>
                <p>{t('success_message_details.0')} <a href="/dashboard/billing?tab=history" className="has-text-info">{t('success_message_details.1')}</a>{t('success_message_details.2')} <a href="/contact" className="has-text-info">{t('success_message_details.3')}</a>.</p>
                <div className="mt-5">
                    <Button
                        label={t('close')}
                        onClick={() => { setPaymentSucceeded(false); window.location.href = window.location.href; }}
                        className="p-button-success"
                    />
                </div>
            </section>
        </Dialog>
    );

    return (
        <>
            <Toast ref={toast} position='bottom-right' />
            <Dialog header="Détails de paiement" dismissableMask={true} visible={visible} onHide={onHide} style={{ width: '100vw', height: '100vh', maxWidth: '1100px', maxHeight: '700px' }} modal>
                <>
                    <article className="message is-info">
                        <div className="message-body">
                            {t('no_payment_info.0')}<a href="https://www.mollie.com" target='_blank' rel="noreferrer">{t('no_payment_info.1')}</a>{t('no_payment_info.2')}<a href="/dashboard/billing?tab=history" target='_blank'>{t('no_payment_info.3')}</a>{t('no_payment_info.4')}
                        </div>
                    </article>

                    <div className="field has-text-centered mb-2">
                        <p>Vous serez redirigé vers la page de paiement sécurisée de Mollie.</p>
                        <hr className='is-invisible' />
                    </div>

                    <label className="label has-text-centered is-size-5" htmlFor="buyingInfos">Information sur l'achat</label>

                    <div name="buyingInfos" className="table-container">
                        <table className="table is-hoverable is-fullwidth">
                            <tbody>
                                <tr><td className="has-text-centered is-width-7"><strong>Formule</strong></td><td>Credits</td></tr>
                                <tr><td className="has-text-centered"><strong>Quantité</strong></td><td>{credits}</td></tr>
                                <tr><td className="has-text-centered"><strong>Prix HT</strong></td><td>{oldTotalCost} €</td></tr>
                                <tr><td className="has-text-centered"><strong>Remise</strong></td><td>{discountAmount ? `${discountAmount}%` : 'Aucune remise appliquée'}</td></tr>
                                <tr><td className="has-text-centered"><strong>Total HT</strong></td><td>{discountAmount ? newTotalCost : oldTotalCost} €</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <label className="label has-text-centered is-size-5" htmlFor="facturation_infos">Adresse de Facturation</label>

                    <div name="facturation_infos mb-1">
                        {selectedFacturation ? (
                            <table className="table is-hoverable is-fullwidth">
                                <tbody>
                                    <tr><td className="has-text-centered is-width-7"><strong>E-mail</strong></td><td>{selectedFacturation.email}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Numéro</strong></td><td>{selectedFacturation.phone_number}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Prénom</strong></td><td>{selectedFacturation.first_name}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Nom</strong></td><td>{selectedFacturation.last_name}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Adresse de facturation</strong></td><td>{selectedFacturation.billing_address}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Adresse de facturation, ligne 2</strong></td><td>{selectedFacturation.additional_billing_address}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Ville</strong></td><td>{selectedFacturation.city}</td></tr>
                                    <tr><td className="has-text-centered"><strong>Code Postal</strong></td><td>{selectedFacturation.postal_code}</td></tr>
                                </tbody>
                            </table>
                        ) : <p className='has-text-centered mb-1'>Veuillez sélectionner une adresse de facturation.</p>}

                        <div className="has-text-centered mb-6">
                            {!selectedFacturation ? (<Button className='mt-2' label="Sélectionner une adresse de facturation" onClick={() => setShowSelectFacturationModal(true)} />) :
                                (<p className='has-text-link is-clickable is-underlined' onClick={() => { setShowSelectFacturationModal(true) }}>Changer d'adresse</p>)
                            }
                        </div>

                        <hr className="is-invisible" />

                    </div>

                    <div className="column" style={{ justifyContent: 'center', textAlign: 'center' }}>
                        <Button className={clsx('button is-success is-light is-outlined', { 'is-loading': isLoading })} onClick={handlePayment} disabled={isLoading || !selectedFacturation}>Finaliser le paiement</Button>
                    </div>
                </>

                <Dialog header="Sélectionner une adresse de facturation" dismissableMask={true} visible={showSelectFacturationModal} onHide={() => setShowSelectFacturationModal(false)} style={{ width: '100vw', height: '100vh', maxWidth: '1200px', maxHeight: '600px' }} modal>
                    <DataTable alwaysShowPaginator={true} emptyMessage={`Aucune adresse de facturation disponible.`} value={companiesFacturation} selectionMode="single" onSelectionChange={(e) => { setSelectedFacturation(e.value); setShowSelectFacturationModal(false); }} selection={selectedFacturation}>
                        <Column field="billing_address" header="Adresse de facturation" />
                        <Column field="additional_billing_address" header="Additionnel" />
                        <Column field="email" header="Email" />
                        <Column field="first_name" header="Prénom" />
                        <Column field="last_name" header="Nom" />
                        <Column field="city" header="Ville" />
                        <Column field="postal_code" header="Code Postal" />
                    </DataTable>

                    <div className="buttons mt-2">
                        <Button label="Créer une nouvelle adresse de facturation" loading={loadingCompaniesFacturation} onClick={() => setShowCreateFacturationModal(true)} />
                        <Button className='ml-1' label="Recharger" severity="info" icon="pi pi-refresh" loading={loadingCompaniesFacturation} onClick={handleReloadCompaniesFacturation} />
                    </div>

                </Dialog>

                <Dialog
                    header="Créer une adresse de facturation"
                    dismissableMask={true}
                    visible={showCreateFacturationModal}
                    onHide={() => setShowCreateFacturationModal(false)}
                    style={{ width: '100vw', height: '100vh', maxWidth: '800px', maxHeight: '700px' }}
                    modal>
                    <hr className="is-invisible" />

                    <div className="columns is-vcentered">
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="email"
                                        name="email"
                                        value={facturationData.email}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="email">E-mail</label>
                                </span>
                            </div>
                        </div>
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="phone_number"
                                        name="phone_number"
                                        value={facturationData.phone_number}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="phone_number">Numéro de téléphone</label>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="columns is-vcentered">
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="first_name"
                                        name="first_name"
                                        value={facturationData.first_name}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="first_name">Prénom</label>
                                </span>
                            </div>
                        </div>
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="last_name"
                                        name="last_name"
                                        value={facturationData.last_name}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="last_name">Nom</label>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="columns is-vcentered">
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="city"
                                        name="city"
                                        value={facturationData.city}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="city">Ville</label>
                                </span>
                            </div>
                        </div>
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="postal_code"
                                        name="postal_code"
                                        value={facturationData.postal_code}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="postal_code">Code postal</label>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="columns is-vcentered">
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="billing_address"
                                        name="billing_address"
                                        value={facturationData.billing_address}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="billing_address">Adresse de facturation</label>
                                </span>
                            </div>
                        </div>
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <InputText
                                        id="additional_billing_address"
                                        name="additional_billing_address"
                                        value={facturationData.additional_billing_address}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="additional_billing_address">Adresse de facturation, ligne 2</label>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="columns is-vcentered">
                        <div className="column">
                            <div className="field">
                                <span className="p-float-label">
                                    <Dropdown
                                        id="country"
                                        name="country"
                                        value={facturationData.country}
                                        options={countryOptions}
                                        onChange={handleCountryChange}
                                        placeholder="Sélectionnez un pays"
                                        filter
                                        filterInputAutoFocus
                                    />
                                    <label htmlFor="country">Pays</label>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="field">
                        <div className="control">
                            <Button
                                label="Créer une adresse de facturation"
                                onClick={handleCreateFacturation}
                            />
                        </div>
                    </div>
                </Dialog>

                <hr className="is-invisible" />
            </Dialog>
        </>
    );
};

export default PaymentDetails;